import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { Moment } from 'moment-timezone';

import Button from 'src/components/core/Button/Button';
import { ActivityMapData } from './ActivityMap';

import styles from './ActivityMapHexDetails.module.scss';

const ActivityMapHexDetailsItem: React.FC<{
  point: ActivityMapData;
  recentCutoffDate: Moment;
  className?: string;
}> = ({ point, recentCutoffDate, className }) => {
  const { t } = useTranslation();
  const isRecent = point.latest_activity.isAfter(recentCutoffDate);

  let recentSummary;
  if (isRecent) {
    // if it is a new conversation, don't mention the highlights
    if (point.conversation_time.isAfter(recentCutoffDate)) {
      recentSummary = t('home.map_summary_conversation');
      // otherwise, the conversation is old, but there are new highlights
    } else if (point.num_highlights_recent > 0) {
      recentSummary = t('home.map_summary_highlight', {
        count: point.num_highlights_recent,
      });
    }
  }

  return (
    <div className={cx('d-flex', { [styles.isRecent]: isRecent }, className)}>
      <div className={styles.bulletPoint} style={{ display: 'none' }}>
        <FontAwesomeIcon icon="circle" />
      </div>
      <div className="flex-grow-1">
        <Link
          className={styles.title}
          to={`/conversation/${point.conversation_id}`}
        >
          {point.title}
        </Link>
        {isRecent && (
          <div className={styles.recentSummary}>{recentSummary}</div>
        )}
      </div>
    </div>
  );
};

const ActivityMapHexDetails: React.FC<{
  points: ActivityMapData[];
  recentCutoffDate: Moment;
  onClear: () => void;
}> = ({ points, recentCutoffDate, onClear }) => {
  const { t } = useTranslation();
  const recentPoints = points.filter((d) =>
    d.latest_activity.isAfter(recentCutoffDate)
  );
  const nonRecentPoints = points.filter(
    (d) => !d.latest_activity.isAfter(recentCutoffDate)
  );

  return (
    <div className={styles.detailsItem}>
      <Button
        icon={['far', 'times']}
        color="plain"
        size="lg"
        className={cx(styles.closeBtn, 'close')}
        title={t('home.map_close_panel')}
        onClick={onClear}
      />
      <h6 className="mb-2_5">{t('home.map_selected')}</h6>
      {recentPoints.length > 0 ? (
        <div className="bg-gray-200 rounded p-2 mt-n1 mx-n2 mb-3">
          <div className={styles.insetHeader}>{t('common.new')}</div>
          {recentPoints.map((point: ActivityMapData, i: number) => (
            <ActivityMapHexDetailsItem
              key={point.conversation_id}
              recentCutoffDate={recentCutoffDate}
              point={point}
              className={i === recentPoints.length - 1 ? '' : 'mb-3'}
            />
          ))}
        </div>
      ) : null}
      {nonRecentPoints.map((point: ActivityMapData, i: number) => (
        <ActivityMapHexDetailsItem
          key={point.conversation_id}
          recentCutoffDate={recentCutoffDate}
          point={point}
          className={i === nonRecentPoints.length - 1 ? '' : 'mb-3'}
        />
      ))}
    </div>
  );
};

export default ActivityMapHexDetails;
